import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cloud,
  Download,
  HardDrive,
  KeyRound,
  Loader2,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import type { PosSyncMode } from '@/offline/db';
import type { DesktopSyncMode } from '@/desktop/types';
import { getElectronAPI } from '@/desktop/electronApi';
import { useAuthStore } from '@/store/authStore';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { useSyncStore } from '@/store/syncStore';
import { authApi, posApi, usersApi } from '@/services/api';
import { runChunkedCatalogSync, isLocalFirstMode } from '@/offline';
import { getApiErrorMessage } from '@/utils/apiError';
import { isCompanyAdmin, isManager } from '@/types';
import EnrollPinModal from '@/components/desktop/EnrollPinModal';
import CatalogSyncModal from '@/components/offline/CatalogSyncModal';

type Step = 'mode' | 'catalog' | 'pin' | 'done';

const MODES: {
  id: DesktopSyncMode;
  title: string;
  detail: string;
  icon: typeof Cloud;
}[] = [
  {
    id: 'Live Online',
    title: 'Online POS',
    detail: 'Always use cloud APIs. Needs internet for sales. PIN still unlocks this PC after reboot.',
    icon: Cloud,
  },
  {
    id: 'Local First Auto Sync',
    title: 'Offline first — auto sync',
    detail: 'Cloud when online; local store when offline; queue uploads automatically when back online.',
    icon: RefreshCw,
  },
  {
    id: 'Offline Always Manual Sync',
    title: 'Offline only — manual sync',
    detail: 'Full local POS. Download catalog now; upload with Sync Now when online.',
    icon: HardDrive,
  },
];

export default function DesktopSetupWizard() {
  const navigate = useNavigate();
  const api = getElectronAPI();
  const { user, company, role } = useAuthStore();
  const setSetupState = useDesktopSessionStore((s) => s.setSetupState);
  const catalogOpen = useSyncStore((s) => s.catalogSyncProgress.open);
  const catalogPhase = useSyncStore((s) => s.catalogSyncProgress.phase);

  const [step, setStep] = useState<Step>('mode');
  const [mode, setMode] = useState<DesktopSyncMode | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [catalogDone, setCatalogDone] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [adminPinned, setAdminPinned] = useState(false);
  const [team, setTeam] = useState<Array<{ name: string; full_name?: string; role?: string; is_active?: number }>>(
    [],
  );
  const [enrolledIds, setEnrolledIds] = useState<string[]>([]);

  const needsCatalog = mode === 'Local First Auto Sync' || mode === 'Offline Always Manual Sync';
  const canFinish = adminPinned;

  const refreshEnrolled = async () => {
    if (!api || !company?.code) return;
    const list = await api.vault.listProfiles(company.code);
    setEnrolledIds(list.map((p) => p.serverUserId));
    const me = user?.name || user?.full_name;
    if (me && list.some((p) => p.serverUserId === me || p.displayName === (user?.full_name || ''))) {
      setAdminPinned(true);
    }
  };

  useEffect(() => {
    if (step === 'pin') {
      void usersApi
        .list()
        .then((res) => {
          const rows = (res.data.message || []) as typeof team;
          setTeam(Array.isArray(rows) ? rows.filter((u) => u.is_active !== 0) : []);
        })
        .catch(() => setTeam([]));
      void refreshEnrolled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const home = useMemo(
    () => (isCompanyAdmin(role) || isManager(role) ? '/dashboard' : '/'),
    [role],
  );

  /** Ensure company header matches auth session before cloud catalog APIs. */
  const ensureCloudSessionForCatalog = async () => {
    if (company?.code) {
      localStorage.setItem('company_code', company.code);
    }
    try {
      await authApi.getCurrentUser();
      useDesktopSessionStore.getState().setCloudSessionExpired(false);
    } catch (e) {
      useDesktopSessionStore.getState().setCloudSessionExpired(true);
      throw new Error(
        getApiErrorMessage(
          e,
          'Cloud session required to download the catalog. Use Online login, then retry.',
        ),
      );
    }
  };

  const catalogErrorMessage = (e: unknown) => {
    const status = (e as { response?: { status?: number } })?.response?.status;
    const msg = getApiErrorMessage(e, 'Catalog download failed');
    if (status === 401 || status === 403) {
      useDesktopSessionStore.getState().setCloudSessionExpired(true);
      return `${msg} Use Online login, then Retry download.`;
    }
    return msg;
  };

  const persistMode = async (chosen: DesktopSyncMode) => {
    useSyncStore.getState().setPosSyncMode(chosen as PosSyncMode);
    try {
      await posApi.updatePosSettings({ pos_sync_mode: chosen });
    } catch {
      /* offline-after setup still applies local store mode */
    }
    await api?.setConfig({
      desktopSyncMode: chosen,
      boundCompanyCode: company?.code || null,
    });
    setSetupState({ desktopSyncMode: chosen, boundCompanyCode: company?.code || null });
  };

  const onChooseMode = async () => {
    if (!mode || !company?.code) {
      setError('Select a mode to continue');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await persistMode(mode);
      if (needsCatalog) {
        setStep('catalog');
        await ensureCloudSessionForCatalog();
        await runChunkedCatalogSync({ force: true });
        setCatalogDone(true);
        setStep('pin');
      } else {
        setCatalogDone(true);
        setStep('pin');
      }
    } catch (e) {
      setError(catalogErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const retryCatalog = async () => {
    setBusy(true);
    setError('');
    try {
      await ensureCloudSessionForCatalog();
      await runChunkedCatalogSync({ force: true });
      setCatalogDone(true);
      setStep('pin');
    } catch (e) {
      setError(catalogErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (!canFinish || !company?.code || !mode) return;
    setBusy(true);
    setError('');
    try {
      await api?.setConfig({
        setupComplete: true,
        boundCompanyCode: company.code,
        desktopSyncMode: mode,
      });
      setSetupState({
        setupComplete: true,
        boundCompanyCode: company.code,
        desktopSyncMode: mode,
        pendingDesktopSetup: false,
      });
      useDesktopSessionStore.setState({ locallyUnlocked: true, locked: false });
      navigate(home, { replace: true });
    } catch (e) {
      setError((e as Error)?.message || 'Could not finish setup');
    } finally {
      setBusy(false);
    }
  };

  if (!api) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Desktop setup requires the Electron app.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <CatalogSyncModal />
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 w-full max-w-lg p-6 space-y-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">
            Desktop setup
          </p>
          <h1 className="text-xl font-bold text-gray-900 mt-1">Configure this PC</h1>
          <p className="text-sm text-gray-500 mt-1">
            Company <strong>{company?.name || company?.code}</strong> — data stays isolated on this
            device. Cloud Frappe stays online; this app uses APIs + local storage.
          </p>
        </div>

        {/* Steps indicator */}
        <div className="flex gap-2 text-[11px] font-medium text-gray-500">
          {(['mode', 'catalog', 'pin'] as Step[]).map((s, i) => (
            <span
              key={s}
              className={`px-2 py-1 rounded-full ${
                step === s ? 'bg-primary-50 text-primary-700' : 'bg-gray-50'
              }`}
            >
              {i + 1}. {s === 'mode' ? 'Mode' : s === 'catalog' ? 'Download' : 'PIN'}
            </span>
          ))}
        </div>

        {step === 'mode' && (
          <div className="space-y-3">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  className={`w-full text-left flex gap-3 p-3 rounded-xl border transition-colors ${
                    active
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{m.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{m.detail}</p>
                  </div>
                </button>
              );
            })}
            <button
              type="button"
              disabled={!mode || busy}
              onClick={() => void onChooseMode()}
              className="btn-primary w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Continue
            </button>
          </div>
        )}

        {step === 'catalog' && (
          <div className="space-y-3 text-center py-4">
            <Download className="w-10 h-10 text-primary-600 mx-auto" />
            <p className="text-sm text-gray-700">
              {catalogOpen || busy
                ? `Downloading catalog… (${catalogPhase})`
                : catalogDone
                  ? 'Catalog ready'
                  : 'Prepare local catalog'}
            </p>
            {error && (
              <button type="button" className="btn-primary w-full py-2" onClick={() => void retryCatalog()}>
                Retry download
              </button>
            )}
          </div>
        )}

        {step === 'pin' && (
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <KeyRound className="w-5 h-5 text-primary-600 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-gray-900">Create unlock PIN</p>
                <p className="text-xs text-gray-500">
                  Your Admin PIN is required to finish. Other users set PIN when they first Online
                  Login on this PC.
                </p>
              </div>
            </div>

            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(team.length ? team : [{ name: user?.name || '', full_name: user?.full_name, role }]).map(
                (u) => {
                  const enrolled = enrolledIds.includes(u.name) ||
                    (u.name === (user?.name || '') && adminPinned);
                  const isMe = u.name === user?.name;
                  return (
                    <div
                      key={u.name || u.full_name}
                      className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <span>
                        {u.full_name || u.name}
                        {isMe ? ' (you)' : ''} · {u.role || '—'}
                      </span>
                      <span className={enrolled ? 'text-emerald-600' : 'text-amber-600'}>
                        {enrolled ? 'PIN set' : isMe ? 'Required' : 'Pending'}
                      </span>
                    </div>
                  );
                },
              )}
            </div>

            {!adminPinned && (
              <button
                type="button"
                onClick={() => setEnrollOpen(true)}
                className="btn-primary w-full py-2.5"
              >
                Set my PIN
              </button>
            )}

            {adminPinned && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void finish()}
                className="btn-primary w-full py-2.5 flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Finish setup & open POS
              </button>
            )}

            {isLocalFirstMode(mode as PosSyncMode) && catalogDone && (
              <p className="text-[11px] text-gray-400 text-center">Local catalog downloaded for offline use.</p>
            )}
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
          </p>
        )}
      </div>

      <EnrollPinModal
        open={enrollOpen}
        required
        title="Set your unlock PIN"
        onClose={() => {
          /* required during setup */
        }}
        onEnrolled={() => {
          setAdminPinned(true);
          setEnrollOpen(false);
          void refreshEnrolled();
        }}
      />
    </div>
  );
}
